import type { Centavos } from "./dinero";

/**
 * PI Barra — el pase del colaborador: su lógica, su puerta y su sesión.
 *
 * La pantalla está en `Pase.tsx`. Aquí vive lo que se puede probar sin pintar
 * nada, que es donde están las decisiones.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * POR QUÉ LA SESIÓN DEL PASE NO USA EL ALMACÉN DE `edge.ts`
 * ═══════════════════════════════════════════════════════════════════════════
 * `edge.ts` guarda el token en una llave de módulo, `tap:token`, y el KDS llama
 * `usarAlmacen("local")` **a nivel de módulo** (`kds/src/App.tsx:60`). Si la
 * pantalla del colaborador se montara ahí y compartiera ese almacén, el token del
 * promotor y el del bartender escribirían **la misma entrada del mismo origen**:
 * entrar como colaborador tiraría la sesión de la barra, y al revés.
 *
 * Así que el pase tiene su propia llave, `tap:pase`. No es paranoia: son dos
 * personas distintas con dos permisos distintos que pueden abrir la misma app en
 * el mismo teléfono.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TODO ACCESO AL ALMACÉN VA EN `try`, Y AQUÍ IMPORTA MÁS QUE EN NINGÚN LADO
 * ═══════════════════════════════════════════════════════════════════════════
 * `edge.ts` protege `token()` y `olvidarToken()` pero **no** `guardarToken()`.
 * Para la barra es un descuido tolerable; para el pase, no: el caso de uso ES un
 * teléfono prestado o en modo privado, donde escribir en `localStorage` LANZA.
 * Las tres funciones de aquí van en `try`.
 */

/** La llave del almacén. Distinta de la de `edge.ts` a propósito. */
const LLAVE = "tap:pase";

/**
 * El pase tal como lo manda `GET /cortesias/mi-pase`.
 *
 * Se declara completo y no con `Record<string, unknown>` porque la pantalla
 * decide qué pinta leyendo estos campos: si el servidor renombra uno, esto tiene
 * que dejar de compilar en vez de pintar un saldo con `undefined` adentro.
 */
export interface PaseDelColaborador {
  apple_wallet_disponible?: boolean;
  google_wallet_disponible?: boolean;
  nombre?: string | null;
  tipo?: 'colaborador' | 'invitado';
  qr_formato?: 'PIC1' | 'PIC2';
  bolsas?: { cortesias: { cupo: number; consumido: number; remanente: number }; covers: { cupo: number; consumido: number; remanente: number } };
  staff_id: string;
  perfil: string | null;
  night_date: string;
  /** Uno de los siete de `cortesias.ESTADOS`. Ver `porQuePuedeEsperar`. */
  estado: string;
  grant_id: string | null;
  credencial: string | null;
  credencial_estado: string | null;
  /** PIC2 es estático. PIC1 cambia cada 30 s. */
  token?: string | null;
  cupo: number;
  consumido: number;
  remanente: number;
  expira_at: string | null;
  productos?: { sku: string; nombre: string; precio_cents?: number }[];
}

/** La sesión que abre la liga. */
export interface SesionDelPase {
  token: string;
  staff_id: string;
  nombre: string;
  perfil: string;
  expira_en: number;
}

/**
 * De dónde salen los datos del pase. Interfaz y no módulo, por la misma razón
 * que `PuertoDelPanel`: quien monta la pantalla decide el transporte, y así
 * `shared/` no depende de ninguna app.
 */
export interface PuertoDelPase {
  /** Canjea la liga. Es la única llamada sin sesión. */
  entrar(token: string): Promise<SesionDelPase>;
  miPase(): Promise<PaseDelColaborador>;
  appleWallet?(): Promise<void>;
  googleWallet?(): Promise<void>;
  /**
   * Dibuja el QR y devuelve el SVG.
   *
   * VA POR AQUÍ Y NO POR UN `import` en `Pase.tsx`, y es una razón medida: hoy el
   * ÚNICO import desnudo de todo `shared/panel` es `react`, y hasta ese necesitó
   * `paths` explícitos en los dos tsconfig porque `shared/` no tiene
   * `node_modules` encima. `qrcode` está en `kiosk` y en el laboratorio pero **NO
   * en `kds`**, así que importarlo aquí obligaría a tocar el `package.json` y el
   * `package-lock` de kds. Quien monta la pantalla ya tiene su librería; que la
   * pase.
   */
  dibujarQR(texto: string): Promise<string>;
}

/* ── La sesión ───────────────────────────────────────────────────────────── */

let sesionEnMemoria: string | null = null;
let soloMemoria = false;

export function sesionGuardada(): string | null {
  try {
    return localStorage.getItem(LLAVE);
  } catch {
    // Modo privado, cookies bloqueadas, o el capturador de miniaturas. Sin
    // sesión se pinta la pantalla de la liga, que es la respuesta correcta.
    return null;
  }
}

/** Identidad activa, incluso si el navegador impide persistirla. */
export function sesionActual(): string | null {
  return soloMemoria ? sesionEnMemoria : sesionGuardada();
}

export function guardarSesion(token: string): void {
  sesionEnMemoria = token;
  soloMemoria = false;
  try {
    localStorage.setItem(LLAVE, token);
  } catch {
    soloMemoria = true;
    // Safari en privado LANZA al escribir. La sesión se queda en memoria: el
    // pase funciona esta vez; al cerrar la pestaña se necesita otra liga. Es
    // mucho mejor que una pantalla en blanco sin explicación.
  }
}

export function olvidarSesion(): void {
  sesionEnMemoria = null;
  soloMemoria = false;
  try {
    localStorage.removeItem(LLAVE);
    localStorage.removeItem("tap:pase-cache");
  } catch {
    /* nada que hacer, y nada que reportar */
  }
}

/* ── La liga en la URL ───────────────────────────────────────────────────── */

/**
 * Saca el token de la liga de una URL.
 *
 * Acepta `?liga=` y `#liga=`. El segundo existe porque **el fragmento no viaja
 * al servidor ni queda en los registros del proxy**, y el árbol tiene doctrina
 * escrita contra poner un token en el query. La liga tiene que ir en la URL —no
 * hay otra forma de que una liga sea una liga— pero el fragmento es el lugar
 * menos malo.
 *
 * Devuelve `null` si no hay token, y NO inventa uno: una URL sin liga es la
 * visita normal de quien ya tiene sesión.
 */
export function ligaDeLaUrl(url: string): string | null {
  let bruto = "";
  try {
    const u = new URL(url, "http://local");
    bruto = u.searchParams.get("liga") ?? "";
    if (!bruto && u.hash) {
      bruto = new URLSearchParams(u.hash.replace(/^#/, "")).get("liga") ?? "";
    }
  } catch {
    return null;
  }
  const limpio = bruto.trim();
  return limpio ? limpio : null;
}

/* ── Lo que la pantalla decide ───────────────────────────────────────────── */

/** ¿Se puede mostrar el QR ahora mismo? */
export function puedeMostrarQR(pase: PaseDelColaborador): boolean {
  return pase.estado === "disponible" && !!pase.credencial && !!pase.token;
}

/**
 * Qué se le dice a la persona en cada estado, y qué puede hacer al respecto.
 *
 * Es una TABLA y no un cálculo sobre el saldo, igual que en el laboratorio: en
 * cuanto fuera un `if` sobre `remanente`, volvería a ser un motor chiquito que
 * puede discrepar del servidor.
 *
 * `accion` es lo que la persona puede hacer. **Nunca es «activar»**: el
 * levantamiento midió que «activar» no existe como verbo en el servidor y que lo
 * que el botón del laboratorio hace son dos POST de gerencia. Manda la decisión
 * del 9-sep: gerencia activa el pase, el colaborador solo lo ve.
 */
export const LETRERO: Record<string, { titulo: string; accion: string }> = {
  disponible: {
    titulo: "Tu pase está listo",
    accion: "Muestra el código en la barra.",
  },
  por_activar: {
    titulo: "Todavía no tienes pase esta noche",
    accion: "Gerencia lo activa. En cuanto lo haga, aparece aquí.",
  },
  por_autorizar: {
    titulo: "Tu pase espera autorización",
    accion: "Gerencia tiene que autorizarlo. Ya está pedido.",
  },
  agotado: {
    titulo: "Ya usaste todas tus cortesías",
    accion: "El pase sigue siendo tuyo; el saldo vuelve mañana.",
  },
  no_habilitado: {
    titulo: "Las cortesías están pausadas",
    accion: "No es algo de tu pase: el venue las tiene en pausa.",
  },
  revocado: {
    titulo: "Tu pase fue revocado",
    accion: "Habla con gerencia si crees que es un error.",
  },
  vencido: {
    titulo: "Tu pase venció",
    accion: "Los pases duran una noche. Gerencia puede darte uno nuevo.",
  },
};

/** El letrero del estado, o uno honesto si el servidor manda algo que no conocemos. */
export function letreroDe(estado: string): { titulo: string; accion: string } {
  return (
    LETRERO[estado] ?? {
      titulo: "No sabemos leer el estado de tu pase",
      accion: `El servidor dice «${estado}» y esta pantalla no lo conoce. Avisa a gerencia.`,
    }
  );
}

/**
 * El color del estado. Tres, y salen del tema del venue.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SON `--panem-*` PORQUE LA PANTALLA VIVE DENTRO DE `.panem-ui`
 * ═══════════════════════════════════════════════════════════════════════════
 * Esta pantalla se monta en su propia app, dentro del contenedor `.panem-ui`
 * (`shared/tokens/venues/panem-ambar-nocturno.css`). Ahí **no existen** los
 * `--color-*` de `tap.css`: esa hoja no la carga esta app.
 *
 * Es exactamente la misma trampa que el commit 4 midió con `--color-exito`, y
 * por eso se repite la lección en vez de darla por aprendida: un token que no
 * existe en el ámbito donde se usa no da error, no da aviso, y no se nota hasta
 * que alguien abre la pantalla y ve el semáforo sin color. La prueba de abajo
 * es lo único que lo impide.
 *
 * El día que llegue otro venue, estos tres nombres no cambian: cambia el bloque
 * de variables que los define. Eso es lo que quiere decir «agnóstica del tema».
 */
export function colorDe(estado: string): string {
  if (estado === "disponible") return "var(--panem-success)";
  if (estado === "revocado" || estado === "vencido") return "var(--panem-danger)";
  return "var(--panem-warning)";
}

/** `5` de `8` → `"5 de 8"`. Un solo lugar para el texto del saldo. */
export function saldoComoTexto(pase: PaseDelColaborador): string {
  return `${pase.remanente} de ${pase.cupo}`;
}

/**
 * Cada cuánto se vuelve a pedir el pase, en milisegundos.
 *
 * 15 000, que es la MITAD del cubo de rotación del QR (30 s en
 * `canje.py:CUBO_SEGUNDOS`). El número y su razón se portan del laboratorio tal
 * cual: con un intervalo más largo que el cubo, el QR en pantalla estaría vencido
 * la mitad del tiempo y el bartender aprendería a desconfiar del lector.
 *
 * NO se toca el periodo del QR desde aquí: eso es W-3 y es otro paso.
 */
export const REFRESCO_MS = 15_000;

/** El precio de un producto del pase, si lo trae, ya como centavos. */
export function precioDe(p: { precio_cents?: number }): Centavos | null {
  return typeof p.precio_cents === "number" ? (p.precio_cents as Centavos) : null;
}

/** Solo el QR estático vigente puede sobrevivir a una pérdida de red.
 * La caché pertenece a la sesión que lo obtuvo; no prueba saldo actual.
 */
export function guardarPase(pase: PaseDelColaborador, sesion: string): void {
  try {
    if (pase.qr_formato === 'PIC2' && puedeMostrarQR(pase)) localStorage.setItem('tap:pase-cache', JSON.stringify({ sesion, pase }));
    else localStorage.removeItem('tap:pase-cache');
  } catch { /* sin almacenamiento se sigue usando en línea */ }
}
export function paseGuardado(sesion: string, ahora = Date.now()): PaseDelColaborador | null {
  try {
    const c = JSON.parse(localStorage.getItem('tap:pase-cache') ?? 'null');
    if (c?.sesion === sesion && c.pase.qr_formato === 'PIC2' && c.pase.token?.startsWith('PIC2:') && puedeMostrarQR(c.pase) && Date.parse(c.pase.expira_at) > ahora) return c.pase;
  } catch { /* una caché dañada no es una credencial */ }
  return null;
}
